// Body-plan-coupled movement: creatures whose SHAPE implies a motion (shape is
// information the player reads). These carry `behavior` instead of `wander`.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { T } from '../tunables'

// Below this speed, keep the last heading instead of recomputing from a near-
// zero velocity — otherwise an oriented body spins to its seed-angle mid-drift.
const HEADING_SPEED_MIN = 0.03

export function creatureBehaviorSystem(
  world: GameWorld,
  player: Entity,
  dt: number,
  elapsed: number,
): void {
  const C = T.creatures
  for (const e of world.with('behavior', 'velocity', 'position')) {
    if (e.absorbing) continue
    const b = e.behavior
    const dx = player.position.x - e.position.x
    const dy = player.position.y - e.position.y

    if (b.kind === 'darter') {
      // Continuous jitter + periodic fast darts (like wander, quicker).
      const s = b.noiseSeed
      e.velocity.x += Math.sin(elapsed * 1.7 + s) * C.darter.driftAmp * dt
      e.velocity.y += Math.cos(elapsed * 1.5 + s * 1.7) * C.darter.driftAmp * dt
      e.velocity.x -= e.velocity.x * C.darter.damping * dt
      e.velocity.y -= e.velocity.y * C.darter.damping * dt

      const d = Math.hypot(dx, dy)
      if (d > 0.0001 && d < C.darter.fleeRadius) {
        // Skittish: dart directly away when the player closes in.
        const nx = -dx / d, ny = -dy / d
        e.velocity.x = nx * C.darter.fleeSpeed
        e.velocity.y = ny * C.darter.fleeSpeed
        b.burstTimer = C.darter.burstIntervalMin
      } else {
        b.burstTimer -= dt
        if (b.burstTimer <= 0) {
          const a = Math.random() * Math.PI * 2
          e.velocity.x = Math.cos(a) * C.darter.burstSpeed
          e.velocity.y = Math.sin(a) * C.darter.burstSpeed
          b.burstTimer = C.darter.burstIntervalMin + Math.random() * C.darter.burstIntervalRange
        }
      }
    } else if (b.kind === 'driller') {
      // Homes gently toward the player — easy DNA that comes to you.
      const d = Math.hypot(dx, dy)
      if (d > 0.0001) {
        const nx = dx / d, ny = dy / d
        const k = Math.min(1, C.driller.accel * dt)
        e.velocity.x += (nx * C.driller.homeSpeed - e.velocity.x) * k
        e.velocity.y += (ny * C.driller.homeSpeed - e.velocity.y) * k
      }
    } else {
      // vault — sessile: damp toward a standstill and stay planted.
      e.velocity.x -= e.velocity.x * C.vault.damping * dt
      e.velocity.y -= e.velocity.y * C.vault.damping * dt
    }

    // Hold heading while nearly stopped (see HEADING_SPEED_MIN).
    if (Math.hypot(e.velocity.x, e.velocity.y) > HEADING_SPEED_MIN) {
      b.heading = Math.atan2(e.velocity.y, e.velocity.x)
    }
  }
}
